import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_corpus-calculator-2/artifacts/596ervi8_SAHI%20DISHA%20FINAL-06.jpeg';

const navLinks = [
  { label: 'Calculator', href: '#calculator', testid: 'nav-calculator-link' },
  { label: 'Visuals', href: '#visuals', testid: 'nav-visuals-link' },
  { label: '12-6 Rule', href: '#rule-12-6', testid: 'nav-12-6-rule-link' },
];

export const LandingNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = useCallback((href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleConsult = useCallback(() => {
    window.open('tel:+919824050200');
  }, []);

  return (
    <>
      {/* Large Centered Logo Banner */}
      <div className="bg-white border-b border-border" data-testid="logo-banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center py-6 sm:py-8">
          <a href="#" data-testid="nav-logo" className="w-full max-w-md sm:max-w-lg md:max-w-xl">
            <img
              src={LOGO_URL}
              alt="Sahi Disha Financial Services Pvt. Ltd."
              className="w-full h-auto object-contain"
            />
          </a>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav
        className={`sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md ${
          scrolled ? 'shadow-sm py-1.5' : 'py-2'
        }`}
        style={{ transition: 'padding 0.2s ease, box-shadow 0.2s ease' }}
        data-testid="landing-nav"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center md:justify-between">
          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="px-3 py-2 text-sm text-foreground hover:bg-secondary rounded-lg"
                style={{ transition: 'background-color 0.15s ease' }}
                data-testid={link.testid}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-2">
            <Button
              className="hidden sm:inline-flex bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl h-9 px-4 text-sm"
              onClick={handleConsult}
              data-testid="nav-consult-expert-button"
            >
              Consult an Expert
            </Button>
            <button
              className="md:hidden p-2 rounded-lg hover:bg-secondary"
              onClick={() => setMobileOpen((prev) => !prev)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
            <div className="max-w-6xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary rounded-lg"
                  data-testid={`${link.testid}-mobile`}
                >
                  {link.label}
                </button>
              ))}
              <Button
                className="w-full mt-2 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl h-9"
                onClick={handleConsult}
                data-testid="nav-consult-expert-button-mobile"
              >
                Consult an Expert
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default LandingNav;
